import { useEffect } from 'react';

import { useWalkthroughStore } from '@core-ui/components';
import { MoniebookLoader } from '@moniebook-web/components/molecules/moniebook-loader';
import { useScreenInfo } from '@moniebook-web/components/shared/hooks';
import { useFeatureFlagStore, useMobileMenuStore } from '@moniebook-web/store';
import {
    createRootElements,
    DOCUMENT_ROOT_NAMES,
    mergeClassNames,
} from '@moniebook-web/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { Outlet, useLocation, useNavigation } from 'react-router';

import { GhostSessionBanner } from '@/features/ghost-session/banner/ghost-session-banner';
import { useSessionTokenPayload } from '@/features/ghost-session/context/session-token-payload-context';

import { SettingsLayout } from './settings-layout';
import { SystemBanner } from './system-banner';
import type { AppLayoutProps } from './types';

export function AppLayout({ sidebar: Sidebar }: Readonly<AppLayoutProps>) {
    const { show, setShow } = useMobileMenuStore();
    const location = useLocation();
    const screenSize = useScreenInfo().screenSize;
    const isDesktop = screenSize === 'desktop';
    const { triggerWalkthrough } = useWalkthroughStore();
    const { isGhostSession } = useSessionTokenPayload();
    const isSettingsPage = location.pathname.startsWith('/dashboard/settings');
    const { state, location: pendingLocation } = useNavigation();
    const { featureFlags } = useFeatureFlagStore();
    const newSidebar = featureFlags?.['moniebook.pos.newSidebar'];

    useEffect(() => {
        const appRoots = createRootElements([
            DOCUMENT_ROOT_NAMES.DROPDOWN_ROOT,
            DOCUMENT_ROOT_NAMES.FULL_MODAL_ROOT,
        ]);

        document.querySelector('#root')?.parentElement?.append(...appRoots);
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: location.pathname/isDesktop are deliberate triggers to close the mobile menu on navigation or breakpoint change, not read in the effect body
    useEffect(() => {
        setShow(false);
    }, [setShow, location.pathname, isDesktop]);

    return (
        <div className='flex h-full flex-col overflow-auto [-webkit-overflow-scrolling:touch]'>
            {isGhostSession && <GhostSessionBanner />}
            {!triggerWalkthrough && <SystemBanner />}

            <SettingsLayout />
            {!isSettingsPage && (
                <div className='flex grow overflow-auto [-webkit-overflow-scrolling:touch]'>
                    {Sidebar && (
                        <div
                            className={mergeClassNames(
                                'desktop:block hidden h-full',
                                newSidebar ? 'w-156.25' : 'w-224',
                            )}>
                            <Sidebar />
                        </div>
                    )}
                    <main className='relative z-1 flex h-full flex-1 flex-col overflow-y-auto overflow-x-hidden bg-white [-webkit-overflow-scrolling:touch] [will-change:scroll-position]'>
                        {state === 'loading' &&
                        pendingLocation !== undefined ? (
                            <MoniebookLoader fullHeight />
                        ) : (
                            <Outlet />
                        )}

                        <AnimatePresence>
                            {show && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className='fixed inset-0 top-[var(--mobile-menu-bottom,0px)] z-10 desktop:hidden bg-modal-overlay-52-arbitrary'
                                    onClick={() => setShow(false)}
                                    key='show'
                                />
                            )}

                            {Sidebar && (
                                <motion.div
                                    initial={{ x: '-120%' }}
                                    {...(show && { animate: { x: '0' } })}
                                    exit={{ x: '-120%' }}
                                    transition={{
                                        duration: 0.5,
                                        ease: [0.43, 0.13, 0.23, 0.96],
                                    }}
                                    className='fixed top-[var(--mobile-menu-bottom,0px)] bottom-0 z-20 desktop:hidden w-screen'
                                    key='side-bar'>
                                    <Sidebar isMobile />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </main>
                    <aside
                        id='root-details-view'
                        className='w-1/4 min-w-320 max-w-400 animate-aside-slide-in-right empty:hidden'
                    />
                </div>
            )}
        </div>
    );
}
